import React, { FC, memo, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { Stack, Tooltip, styled } from '@mui/material';

import { PostgresFilePointer, S3FilePointer } from '@/api/data-contracts';
import { ReactComponent as ImagePlaceholderIcon } from '@/assets/icons/PictureAsPdfRounded.svg';
import { MoreArtworkButton } from '@/features/media/ui/TableCellFilesPreview/TableCellFilesPreview.styles';
import { useSnackbar } from '@/shared/lib/hooks/useAppSnackbar';
import { ImageResource, usePrefetchResource } from '@/shared/lib/hooks/useResourceImage';
import { useFileDialog } from '@/shared/ui/FileDialogContext/FileDialogContext';

export type CellPdfResource =
  | { kind: 'space-layout-sketch'; spaceId: string }
  | { kind: 'service-task-layout-sketch'; taskId: string }
  | { kind: 'order-document'; orderId: string };

interface Props {
  files: (PostgresFilePointer | S3FilePointer)[];
  resource: CellPdfResource;
}

const MAX_VISIBLE_FILES = 4;

const isSameResource = (a: CellPdfResource, b: CellPdfResource): boolean => {
  if (a.kind !== b.kind) return false;
  switch (a.kind) {
    case 'space-layout-sketch':
      return a.spaceId === (b as typeof a).spaceId;
    case 'service-task-layout-sketch':
      return a.taskId === (b as typeof a).taskId;
    case 'order-document':
      return a.orderId === (b as typeof a).orderId;
  }
};

const fileKey = (file: PostgresFilePointer | S3FilePointer, index: number): string =>
  file.objectId?.toString() ?? file.name ?? String(index);

const CustomCellPdf: FC<Props> = ({ files, resource }) => {
  const { t } = useTranslation();
  const prefetch = usePrefetchResource();
  const { showFile } = useFileDialog();
  const { enqueueSnackbar } = useSnackbar();
  const requestIdRef = useRef(0);

  const viewFile = useCallback(
    async (file: PostgresFilePointer | S3FilePointer) => {
      const documentId = file.objectId?.toString();
      if (!documentId) return;

      const requestId = ++requestIdRef.current;

      try {
        const blob = await prefetch(toImageResource(resource, documentId));
        if (requestId !== requestIdRef.current) return;

        showFile(
          new File([blob], file.name ?? 'layout-sketch.pdf', {
            type: blob.type || 'application/pdf',
          }),
        );
      } catch (error: unknown) {
        if (requestId !== requestIdRef.current) return;

        const status = (error as { response?: { status?: number } })?.response?.status;
        if (
          resource.kind === 'space-layout-sketch' ||
          resource.kind === 'service-task-layout-sketch'
        ) {
          if (status === 403) {
            enqueueSnackbar(t('errors.layout_sketch_forbidden'), { variant: 'error' });
          } else if (status === 404) {
            enqueueSnackbar(t('errors.layout_sketch_not_found'), { variant: 'error' });
          } else {
            enqueueSnackbar(t('errors.layout_sketch_load_failed'), { variant: 'error' });
          }
          return;
        }

        enqueueSnackbar(t('Error loading files'), { variant: 'error' });
      }
    },
    [prefetch, resource, showFile, enqueueSnackbar, t],
  );

  const visibleFiles = files?.slice(0, MAX_VISIBLE_FILES) ?? [];
  const hiddenCount = Math.max((files?.length ?? 0) - MAX_VISIBLE_FILES, 0);

  return (
    <Stack direction="row" alignItems="center">
      {files?.length ? (
        <Stack direction="row" alignItems="center" ml="0.125rem">
          {visibleFiles.map((file, index) => (
            <Tooltip key={fileKey(file, index)} title={file.name} placement="top">
              <div style={{ cursor: 'pointer' }}>
                <PdfThumbnail onClick={() => void viewFile(file)} />
              </div>
            </Tooltip>
          ))}
          {hiddenCount > 0 && <MoreArtworkButton>+{hiddenCount}</MoreArtworkButton>}
        </Stack>
      ) : (
        <p>{t('not_uploaded')}</p>
      )}
    </Stack>
  );
};

export default memo(CustomCellPdf, (prevProps, nextProps) => {
  const filesEqual =
    prevProps.files === nextProps.files ||
    (prevProps.files?.length === nextProps.files?.length &&
      prevProps.files?.every(
        (file, index) =>
          file.objectId === nextProps.files?.[index]?.objectId &&
          file.name === nextProps.files?.[index]?.name,
      )) ||
    false;

  return filesEqual && isSameResource(prevProps.resource, nextProps.resource);
});

const PdfThumbnail = styled(ImagePlaceholderIcon)({
  width: '1.25rem',
  height: '1.25rem',
  objectFit: 'cover',
  borderRadius: '0.25rem',
  boxShadow: '0px 1px 3px 0px rgba(0, 0, 0, 0.10), 0px 1px 5px 0px rgba(0, 0, 0, 0.06)',
});

// The viewer needs the original PDF: the plain service-task layout sketch kind is the PNG
// rendered for Match Artwork, which the PDF viewer rejects as "Invalid PDF structure".
export const toImageResource = (resource: CellPdfResource, documentId: string): ImageResource => {
  switch (resource.kind) {
    case 'space-layout-sketch':
      return { kind: 'space-layout-sketch', spaceId: resource.spaceId, documentId };
    case 'service-task-layout-sketch':
      return { kind: 'service-task-layout-sketch-original', taskId: resource.taskId, documentId };
    case 'order-document':
      return { kind: 'order-document', orderId: resource.orderId, documentId };
  }
};
